import React, { useEffect, useState } from "react";
import { Button, Popover, Typography, Space, Tag, Badge, Avatar, Tooltip, Grid } from "antd";
import { t } from "i18next";
import ApiService from "../../../core/service/ApiService";
import DataGrid from "../../../core/components/data-grid/DataGrid";
import { useDataGrid } from "../../../core/components/data-grid/DataGridContext";
import { useModal } from "../../../core/context/ModalContext";
import Helper from "../../../core/Helper";
import { CheckCircleOutlined, CloseCircleOutlined, InfoCircleOutlined, UserOutlined, DollarOutlined, SendOutlined, StopOutlined, CheckOutlined, MedicineBoxOutlined, BankOutlined } from '@ant-design/icons';
import { PERMISSIONS, useUser } from "../../../core/context/UserContext";
import { AppointmentEntitlementInput } from "./AppointmentEntitlementInput";
import { EntitlementSummaryRow } from "./EntitlementSummaryRow";
import AppointmentStateColumn from "../appointment-datagrid/AppointmentStateColumn";
import AppointmentIdColumn, { AppointmentLink } from "../appointment-datagrid/AppointmentIdColumn";
import UserSelectList from "../../user/UserSelectList";
import EntitlementConfirmationForm from "./EntitlementConfirmationForm";
import { StatusSortableHeader } from "./StatusSortableHeader";
import AppointmentCard, { AppointmentCardField } from "../AppointmentCard";
import AppointmentPreviewApp from "../preview/AppointmentPreviewApp";
import MobileActionsMenu from "../appointment-datagrid/MobileActionsMenu";
import AppointmentExcelButton from "../appointment-datagrid/AppointmentExcelButton";

const { Text } = Typography;

export default function AppointmentEntitlementDataGrid({ hideConfirmButton = false, toolbarExtra, excelView }) {
    const { openModal, closeModal } = useModal();
    const { setData, setPaginationResult, queryModel, setDataGridProps, refreshDataGrid, selectedRows, setSelectedRows, onFilterPageSort } = useDataGrid();
    const { hasPermission, formatPrice, user } = useUser()
    const isMobile = window.innerWidth <= 768;
    const screens = Grid.useBreakpoint();

    const load = async () => {
        const input = {
            ...queryModel,
            filter: {
                ...queryModel.filter,
                startDate: Helper.Date.beginOfDay(queryModel.filter.startDate),
                endDate: Helper.Date.endOfDay(queryModel.filter.endDate),
            }
        }

        if (!Object.keys(input.filter).includes('isDeleted')) {
            input.filter.isDeleted = false
        }

        const res = await ApiService.Entitlement.GetEntitlementDataGrid(input);
        if (res.success) {
            setData(res.data)
            setPaginationResult(res.pagination)
        }
    }

    // --- BUTON FONKSİYONLARI ---
    const selectUserModal = () => {
        const component = (
            <EntitlementConfirmationForm
                appointments={selectedRows}
                onOk={() => {
                    setSelectedRows([])
                    closeModal()
                    refreshDataGrid()
                }}
            />
        )
        openModal(t('Select User for Confirmation'), component, () => { })
    }

    const confirmEntitlements = async () => {
        const res = await ApiService.Entitlement.ConfirmEntitlements({
            appointmentIdList: selectedRows.map(x => x.appointmentId),
        })
        if (res.success) {
            setSelectedRows([])
            refreshDataGrid()
        }
    }

    const cancelEntitlements = async () => {
        const res = await ApiService.Entitlement.CancelConfirmation({
            appointmentIdList: selectedRows.map(x => x.appointmentId),
        })

        if (res.success) {
            setSelectedRows([])
            refreshDataGrid()
        }
    }

    const getColumns = () => {
        return [
            // 1. APPOINTMENT ID (Yeni Eklendi)
            {
                width: 90,
                key: 'appointmentId',
                dataIndex: 'appointmentId',
                title: t('Id'),
                render: (text, row) => <AppointmentIdColumn text={text} row={row} />
            },

            // 2. HASTA BİLGİLERİ (Güncellendi)
            {
                key: 'patientName',
                dataIndex: 'patientName',
                title: t('Patient'),
                width: 200,
                render: (_, row) => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Avatar
                            size={24}
                            icon={<UserOutlined />}
                        />
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <AppointmentLink row={row}>
                                <Text strong style={{ fontSize: 13, color: 'inherit' }}>{row?.patientName} {row?.patientLastname}</Text>
                            </AppointmentLink>
                            <Space size={4}>
                                <Text type="secondary" style={{ fontSize: 11 }}>{row?.patientCustomId}</Text>
                            </Space>
                        </div>
                    </div>
                )
            },

            // 2. TARİH
            {
                key: 'startDate',
                width: 130,
                dataIndex: 'startDate',
                sorter: true,
                title: t('Date'),
                render: (text, row) => (
                    <AppointmentLink row={row}>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontWeight: 500 }}>{Helper.Date.dateFormat(text).split(' ')[0]}</span>
                            <span style={{ fontSize: 11, color: '#888' }}>{Helper.Date.dateFormat(text).split(' ')[1]}</span>
                        </div>
                    </AppointmentLink>
                )
            },

            // 3. BİRİM
            {
                key: 'unitName',
                dataIndex: 'unitName',
                columnName: 'unit.name',
                sorter: true,
                title: t('Unit'),
                width: 150,
                render: (text) => (
                    <Space>
                        <MedicineBoxOutlined style={{ color: '#1890ff' }} />
                        <Text style={{ fontSize: 13 }}>{text}</Text>
                    </Space>
                )
            },

            // 4. KURUM
            {
                key: 'institutionName',
                dataIndex: 'institutionName',
                columnName: 'institution.name',
                sorter: true,
                title: t('Institution'),
                width: 150,
                render: (text) => (
                    <Space>
                        <BankOutlined style={{ color: '#fa8c16' }} />
                        <Tooltip title={text}>
                            <Text style={{ maxWidth: 130 }} ellipsis>{text}</Text>
                        </Tooltip>
                    </Space>
                )
            },

            // 5. SORUMLU KİŞİ
            {
                key: 'institutionUserName',
                dataIndex: 'institutionUserName',
                title: t('Responsible'),
                width: 130,
                render: (text, row) => {
                    if (hasPermission(PERMISSIONS.ENTITLEMENT_UPDATE_APPOINTMENT_RESPONSIBLE_USER)) {
                        return (
                            <UserSelectList
                                value={row?.institutionUserId}
                                placeholder={t('Select')}
                                bordered={false}
                                style={{ borderBottom: '1px solid #d9d9d9', width: '100%' }}
                                onChange={async (value) => {
                                    const res = await ApiService.Entitlement.UpdateAppointmentResponsible(row?.appointmentId, value)
                                    if (res.success) {
                                        await refreshDataGrid()
                                    }
                                }}
                            />
                        )
                    }
                    return <Text type="secondary">{text || '-'}</Text>
                }
            },

            // 6. DURUM
            {
                key: 'state',
                dataIndex: 'state',
                title: t('State'),
                width: 60,
                render: (_, row) => <AppointmentStateColumn text={row?.stateName} row={row} />
            },

            // 7. FİYAT
            {
                key: 'totalPrice',
                dataIndex: 'totalPrice',
                sorter: true,
                title: t('Price'),
                width: 110,
                align: 'right',
                render: (text, row) => (
                    <Text type="secondary">
                        {formatPrice(text, { currency: row?.currency || 'TRY' })}
                    </Text>
                )
            },

            // 8. BAKİYE
            {
                key: 'balance',
                dataIndex: 'balance',
                sorter: true,
                title: t('Balance'),
                width: 140,
                align: 'right',
                render: (text, row) => (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
                        <Text strong style={{ color: row.balance > 0 ? '#cf1322' : '#389e0d' }}>
                            {formatPrice(text, { currency: row?.currency || 'TRY' })}
                        </Text>
                        <Popover
                            overlayStyle={{ maxWidth: 350 }}
                            content={(
                                <div style={{ fontSize: 13 }}>
                                    <div style={{ marginBottom: 8 }}><b>Type:</b> {row?.appointmentTypeName}</div>
                                    <div style={{ marginBottom: 8 }}><b>Payment:</b> {row?.paymentMethods || '-'}</div>
                                    <div><b>Note:</b> <span dangerouslySetInnerHTML={{ __html: row?.institutionPrivateNote || '-' }} /></div>
                                </div>
                            )}
                            trigger="click"
                        >
                            <Button type="text" shape="circle" size="small" icon={<InfoCircleOutlined style={{ color: '#1890ff', fontSize: 12 }} />} />
                        </Popover>
                    </div>
                )
            },

            // 9. HAKEDİŞ TUTARI (SAĞA SABİT - 1)
            {
                title: (
                    <Space size={4}>
                        <DollarOutlined style={{ color: '#1890ff' }} />
                        {t('Entitlement')}
                    </Space>
                ),
                // dataIndex servisten dönen alan (entitlementAmount), sıralama adı ise
                // entity yolu (appointmentEntitlement.amount) — diğer kolonlarla aynı kalıp.
                dataIndex: 'entitlementAmount',
                key: 'entitlementAmount',
                columnName: 'appointmentEntitlement.amount',
                sorter: true,
                width: 150,
                className: 'bg-yellow-50',
                render: (text, row) => {
                    if (hasPermission(PERMISSIONS.ENTITLEMENT_MANAGER)) {
                        return (
                            <div style={{ border: '1px solid #d9d9d9', borderRadius: 4, padding: 2, backgroundColor: '#fff' }}>
                                <AppointmentEntitlementInput
                                    row={row}
                                    defaultValue={text}
                                    onSuccess={() => refreshDataGrid()}
                                />
                            </div>
                        );
                    }
                    return <Text strong>{text != null ? formatPrice(text, { currency: row?.entitlementCurrency || row?.currency || 'TRY' }) : '-'}</Text>;
                },
            },

            // 10-11. DURUM (BİRLEŞTİRİLMİŞ)
            {
                // title kısmına text yerine bileşen veriyoruz
                title: <StatusSortableHeader />,
                key: 'statusCombined',
                width: 200, // Selectbox sığsın diye biraz genişlettim
                align: 'center',
                fixed: isMobile ? false : 'right',
                sorter: false, // ÖNEMLİ: Standart sıralamayı kapatıyoruz, kendi header'ımız yönetecek.
                render: (_, row) => {
                    let confirmStatusNode;
                    if (row.confirmStatus === 1) {
                        confirmStatusNode = (
                            <div style={{}}>
                                <Badge status="processing" text={<span style={{ color: '#fa8c16' }}>{t('Waiting')}</span>} />
                                <br />
                                <Text style={{ marginLeft: 12 }}>{row?.confirmUserName}</Text>
                            </div>
                        )
                    }
                    else if (row.confirmStatus === 2) {
                        confirmStatusNode = (
                            <>
                                <Tag icon={<CheckCircleOutlined />} color="success" style={{ width: '100%' }}>
                                    <Popover
                                        content={(
                                            <>
                                                {row?.confirmUserName}
                                                <br />
                                                {new Date(row?.confirmStatusDate).toLocaleString()}
                                            </>
                                        )}
                                    >
                                        {t('Confirmed')}
                                    </Popover>
                                </Tag>
                                {
                                    row.entitlementCompleteDate && (
                                        <Tag color="success" style={{ width: '100%' }}>
                                            {new Date(row.entitlementCompleteDate).toLocaleString()}
                                        </Tag>
                                    )
                                }

                            </>
                        )
                    }
                    else if (row.confirmStatus === 3) {
                        confirmStatusNode = (
                            <>
                                <Tag icon={<CloseCircleOutlined />} color="error" style={{ width: '100%' }}>
                                    <Popover
                                        content={new Date(row?.confirmStatusDate).toLocaleString()}
                                    >
                                        {t('Rejected')}
                                    </Popover>
                                    <br />
                                    {row?.confirmUserName}
                                </Tag>
                            </>
                        )
                    }
                    else {
                        confirmStatusNode = (
                            <Tag><span style={{ color: '#ccc', width: '100%' }}>-</span></Tag>
                        )
                    }

                    return (
                        <Space direction="vertical" size={2} style={{ width: '100%', textAlign: 'left' }} alignItems={'start'}>
                            <div style={{ cursor: 'pointer' }}>{confirmStatusNode}</div>
                        </Space>
                    );
                },
            },
        ]
    }

    useEffect(() => {
        load()
    }, [queryModel.updateKey])

    // --- MOBİL KART: randevu kartı + hakediş alanları (sorumlu, hakediş tutarı, onay durumu) ---
    const openAppointmentPreview = (row) => {
        openModal(
            t('Appointment Preview'),
            <AppointmentPreviewApp appointmentId={row.appointmentId} onDelete={() => refreshDataGrid()} />,
            () => refreshDataGrid()
        );
    };

    const renderConfirmStatus = (row) => {
        if (row.confirmStatus === 1) {
            return <Tag color="processing" style={{ margin: 0 }}>{t('Waiting')}{row?.confirmUserName ? ` · ${row.confirmUserName}` : ''}</Tag>
        }
        if (row.confirmStatus === 2) {
            return (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                    <Tag icon={<CheckCircleOutlined />} color="success" style={{ margin: 0 }}>
                        {t('Confirmed')}{row?.confirmUserName ? ` · ${row.confirmUserName}` : ''}
                    </Tag>
                    {row.entitlementCompleteDate && (
                        <Text type="secondary" style={{ fontSize: 11 }}>{new Date(row.entitlementCompleteDate).toLocaleString()}</Text>
                    )}
                </div>
            )
        }
        if (row.confirmStatus === 3) {
            return <Tag icon={<CloseCircleOutlined />} color="error" style={{ margin: 0 }}>{t('Rejected')}{row?.confirmUserName ? ` · ${row.confirmUserName}` : ''}</Tag>
        }
        return <Text type="secondary">-</Text>
    }

    const renderCard = (row) => {
        const canPreview = hasPermission(PERMISSIONS.APPOINTMENT_VIEW_OTHER_USER) || row.responsibleUserId === user?.id;
        return (
            <AppointmentCard
                row={row}
                formatPrice={formatPrice}
                showPatient={true}
                showPreview={true}
                canPreview={canPreview}
                onPreview={openAppointmentPreview}
            >
                <AppointmentCardField label={t('Responsible')}>
                    {hasPermission(PERMISSIONS.ENTITLEMENT_UPDATE_APPOINTMENT_RESPONSIBLE_USER) ? (
                        <UserSelectList
                            value={row?.institutionUserId}
                            placeholder={t('Select')}
                            style={{ width: '100%' }}
                            onChange={async (value) => {
                                const res = await ApiService.Entitlement.UpdateAppointmentResponsible(row?.appointmentId, value)
                                if (res.success) await refreshDataGrid()
                            }}
                        />
                    ) : <Text>{row?.institutionUserName || '-'}</Text>}
                </AppointmentCardField>
                <AppointmentCardField label={t('Entitlement')}>
                    {hasPermission(PERMISSIONS.ENTITLEMENT_MANAGER) ? (
                        <AppointmentEntitlementInput row={row} defaultValue={row.entitlementAmount} onSuccess={() => refreshDataGrid()} block />
                    ) : (
                        <Text strong>{row.entitlementAmount != null ? formatPrice(row.entitlementAmount, { currency: row?.entitlementCurrency || row?.currency || 'TRY' }) : '-'}</Text>
                    )}
                </AppointmentCardField>
                <AppointmentCardField label={t('Confirmation')}>
                    {renderConfirmStatus(row)}
                </AppointmentCardField>
                {row?.paymentMethods && (
                    <AppointmentCardField label={t('Payment Method')}>
                        <Text type="secondary" style={{ fontSize: 12, textAlign: 'right' }}>{row.paymentMethods}</Text>
                    </AppointmentCardField>
                )}
            </AppointmentCard>
        );
    };

    useEffect(() => {
        let columns = getColumns()
        let props = {
            rowKey: 'appointmentId',
            enableRowSelection: true,
            expandedRowRender: false,
            columns: columns,
            rowClassName: (row) => row.isDeleted ? 'deleted-row opacity-50' : '',
            cardRender: renderCard,
            virtual: true,
            virtualRowHeight: 47, // size: 'middle'
            antdTableProps: {
                summary: () => (<EntitlementSummaryRow />),
                scroll: { x: 1500 }, // Sütunlar arttığı için genişletildi
                size: 'middle'
            }
        }
        setDataGridProps(props)
    }, [])

    // Mobil: üç işlem + Excel tek "İşlemler" menüsünde (yenile + anahtarlarla aynı satırın sağında, altına açılır)
    const renderMobileToolbar = () => {
        const noSelection = selectedRows?.length === 0
        const items = [
            hasPermission(PERMISSIONS.ENTITLEMENT_CONFIRM) && {
                key: 'confirm', icon: <CheckOutlined />, label: t('Confirm Selected'), disabled: noSelection, onClick: confirmEntitlements,
            },
            !hideConfirmButton && hasPermission(PERMISSIONS.ENTITLEMENT_MANAGER) && {
                key: 'send', icon: <SendOutlined />, label: t('Send for Confirmation'),
                disabled: noSelection || selectedRows?.filter(x => x?.entitlementCompleteDate)?.length > 0,
                onClick: selectUserModal,
            },
            hasPermission(PERMISSIONS.ENTITLEMENT_MANAGER) && {
                key: 'cancel', icon: <StopOutlined />, label: t('Cancel Request'), danger: true, disabled: noSelection, onClick: cancelEntitlements,
            },
        ].filter(Boolean)
        return <MobileActionsMenu items={items} excelView={excelView} />
    }

    const renderToolbar = () => screens.md === false ? renderMobileToolbar() : (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', flex: 1 }}>
            {hasPermission(PERMISSIONS.ENTITLEMENT_CONFIRM) && (
                <Button type="primary" icon={<CheckOutlined />} disabled={selectedRows?.length === 0} size="small" onClick={confirmEntitlements}>
                    {t('Confirm Selected')}
                </Button>
            )}
            {!hideConfirmButton && hasPermission(PERMISSIONS.ENTITLEMENT_MANAGER) && (
                <Button icon={<SendOutlined />} disabled={selectedRows?.filter(x => x?.entitlementCompleteDate)?.length > 0 || selectedRows?.length === 0} size="small" onClick={selectUserModal}>
                    {t('Send for Confirmation')}
                </Button>
            )}
            {hasPermission(PERMISSIONS.ENTITLEMENT_MANAGER) && (
                <Button danger icon={<StopOutlined />} disabled={selectedRows?.length === 0} size="small" onClick={cancelEntitlements}>
                    {t('Cancel Request')}
                </Button>
            )}
            {/* Excel en sağda */}
            <AppointmentExcelButton view={excelView} />
        </div>
    );

    return (
        <div>
            <DataGrid name={'appointments-entitlement'} title={renderToolbar} toolbarExtra={toolbarExtra} />
        </div>
    )
}